import { useAuth } from "../../context/AuthContext";
import { useOrgContext } from "../../context/OrgContext";
import { useTheme } from "../../context/ThemeContext";
import { useLocale } from "../../context/LanguageContext";
import { LogOut, Moon, Sun, ChevronDown, Menu } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router";
import type { ManagedOrganization } from "../../types";
import type { Locale } from "../../i18n";

export function Topbar({ onMenuToggle }: { onMenuToggle?: () => void }) {
  const { user, logout } = useAuth();
  const { currentOrg, managedOrgs } = useOrgContext();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { locale, setLocale, t } = useLocale();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { pathname } = useLocation();

  /** Switch to a different org by navigating to the same sub-path under the new slug */
  const handleOrgSwitch = (org: ManagedOrganization) => {
    if (orgSlug) {
      // Replace current org slug in the path with the new one
      const subPath = pathname.slice(`/${orgSlug}`.length) || "/";
      navigate(`/${org.slug}${subPath === "/" ? "" : subPath}`);
    } else {
      // Currently on admin/global route → navigate to org's dashboard
      navigate(`/${org.slug}`);
    }
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 md:h-16 md:px-6">
      {/* Left: hamburger (mobile) + Org switcher */}
      <div className="flex items-center gap-3">
        {onMenuToggle && (
          <button
            type="button"
            onClick={onMenuToggle}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground lg:hidden"
            aria-label={t("nav.openMenu")}
          >
            <Menu size={20} />
          </button>
        )}
        {managedOrgs.length > 1 && (
          <OrgSwitcher
            currentOrg={currentOrg}
            managedOrgs={managedOrgs}
            onSelect={handleOrgSwitch}
            t={t}
          />
        )}
        {managedOrgs.length === 1 && currentOrg && (
          <span className="text-sm font-medium text-foreground">
            {currentOrg.name}
          </span>
        )}
      </div>

      {/* Right: Language toggle + Theme toggle + user menu */}
      <div className="flex items-center gap-1.5 md:gap-3">
        <span className="hidden sm:contents">
          <LanguageToggle locale={locale} setLocale={setLocale} t={t} resolvedTheme={resolvedTheme} />
        </span>
        <ThemeToggle theme={theme} resolvedTheme={resolvedTheme} setTheme={setTheme} t={t} />
        <UserMenu user={user} onLogout={logout} t={t} />
      </div>
    </header>
  );
}

/* ─── Org Switcher ─── */

interface OrgSwitcherProps {
  currentOrg: { _id: string; name: string; picture?: string } | null;
  managedOrgs: ManagedOrganization[];
  onSelect: (org: ManagedOrganization) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

function OrgSwitcher({ currentOrg, managedOrgs, onSelect, t }: OrgSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
      >
        {currentOrg?.name ?? t("topbar.selectOrganization")}
        <ChevronDown size={14} />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-lg border border-border bg-card shadow-lg">
          {managedOrgs.map((org) => (
            <button
              key={org._id}
              type="button"
              onClick={() => {
                onSelect(org);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-muted ${
                org._id === currentOrg?._id
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-foreground"
              }`}
            >
              {org.picture ? (
                <img
                  src={org.picture}
                  alt=""
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                  {org.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="truncate">{org.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Language Toggle ─── */

const LOCALE_OPTIONS: { value: Locale; label: string; ariaKey: string }[] = [
  { value: "de", label: "DE", ariaKey: "topbar.switchTo.de" },
  { value: "en", label: "EN", ariaKey: "topbar.switchTo.en" },
  { value: "tr", label: "TR", ariaKey: "topbar.switchTo.tr" },
];

function LanguageToggle({
  locale,
  setLocale,
  t,
  resolvedTheme,
}: {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string) => string;
  resolvedTheme: "light" | "dark";
}) {
  const isLight = resolvedTheme === "light";
  return (
    <div
      className={`flex h-10 items-center gap-1 rounded-2xl p-1 backdrop-blur-sm ${
        isLight
          ? "border border-border/80 bg-muted/70 shadow-sm"
          : "border border-white/15 bg-white/10 shadow-[0_2px_10px_rgba(2,6,23,0.25)]"
      }`}
      role="radiogroup"
      aria-label={t("topbar.language")}
    >
      {LOCALE_OPTIONS.map(({ value, label, ariaKey }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={locale === value}
          aria-label={t(ariaKey)}
          onClick={() => setLocale(value)}
          className={`flex h-8 w-11 items-center justify-center rounded-full ring-1 ring-transparent text-xs font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 ${
            isLight ? "focus-visible:ring-primary/35" : "focus-visible:ring-white/70"
          } ${
            locale === value
              ? isLight
                ? "bg-card text-foreground shadow-sm ring-border"
                : "bg-white text-foreground shadow-sm ring-white/70"
              : isLight
                ? "text-muted-foreground hover:bg-background hover:text-foreground"
                : "text-muted-foreground hover:bg-white/18 hover:text-foreground"
          }`}
          title={t(ariaKey)}
        >
          {value === "de" ? (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#ffce00" />
              <rect width="24" height="10.67" fill="#dd0000" />
              <rect width="24" height="5.33" fill="#000000" />
            </svg>
          ) : value === "tr" ? (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#e30a17" />
              <circle cx="10.5" cy="8" r="3.2" fill="#ffffff" />
              <circle cx="11.6" cy="8" r="2.6" fill="#e30a17" />
              <path
                d="M16.1 8 14.63 7.47 14.58 5.91 13.62 7.14 12.12 6.71 13.0 8 12.12 9.29 13.62 8.86 14.58 10.09 14.63 8.53Z"
                fill="#ffffff"
              />
            </svg>
          ) : (
            <svg className="h-4.5 w-6 rounded-[2px]" viewBox="0 0 24 16" aria-hidden="true">
              <rect width="24" height="16" fill="#0a47a1" />
              <path d="M0 0L24 16M24 0L0 16" stroke="#ffffff" strokeWidth="3.4" />
              <path d="M0 0L24 16M24 0L0 16" stroke="#cf142b" strokeWidth="1.8" />
              <path d="M12 0v16M0 8h24" stroke="#ffffff" strokeWidth="5.2" />
              <path d="M12 0v16M0 8h24" stroke="#cf142b" strokeWidth="3" />
            </svg>
          )}
          <span className="sr-only">{label}</span>
        </button>
      ))}
    </div>
  );
}

/* ─── Theme Toggle ─── */

const THEME_OPTIONS = [
  { value: "light" as const, icon: Sun, labelKey: "topbar.light" },
  { value: "dark" as const, icon: Moon, labelKey: "topbar.dark" },
] as const;

function ThemeToggle({
  theme,
  resolvedTheme,
  setTheme,
  t,
}: {
  theme: string;
  resolvedTheme: "light" | "dark";
  setTheme: (t: "light" | "dark" | "system") => void;
  t: (key: string) => string;
}) {
  return (
    <div
      className="flex h-9 items-center gap-0.5 rounded-lg border border-border bg-muted/50 p-0.5"
      role="radiogroup"
      aria-label={t("topbar.theme")}
    >
      {THEME_OPTIONS.map(({ value, icon: Icon, labelKey }) => {
        const isActive = theme === value || (theme === "system" && resolvedTheme === value);
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={theme === value}
            onClick={() => setTheme(value)}
            className={`flex h-7 w-7 items-center justify-center rounded-md transition-all ${
              isActive
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
            title={t(labelKey)}
          >
            <Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

/* ─── User Menu ─── */

function UserMenu({
  user,
  onLogout,
  t,
}: {
  user: { firstName: string; lastName: string; email: string; profilePicture?: string } | null;
  onLogout: () => Promise<void>;
  t: (key: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Reset error state if profilePicture URL changes
  useEffect(() => {
    setImgError(false);
  }, [user?.profilePicture]);

  if (!user) return null;

  const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  const showAvatar = user.profilePicture && !imgError;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted"
      >
        {showAvatar ? (
          <img
            src={user.profilePicture}
            alt=""
            className="h-8 w-8 rounded-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
            {initials}
          </div>
        )}
        <span className="hidden text-sm font-medium text-foreground md:inline">
          {user.firstName}
        </span>
        <ChevronDown size={14} className="text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-card shadow-lg">
          <div className="flex items-center gap-3 border-b border-border px-4 py-3">
            {showAvatar ? (
              <img
                src={user.profilePicture}
                alt=""
                className="h-9 w-9 rounded-full object-cover"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {user.firstName} {user.lastName}
              </p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void onLogout();
            }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-destructive hover:bg-muted"
          >
            <LogOut size={16} />
            {t("topbar.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}
